import { type FormEvent, useEffect, useState } from "react";
import {
  AlertTriangle,
  KeyRound,
  LoaderCircle,
  LogOut,
  RefreshCw,
  ShieldCheck,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ADMIN_TOKEN_HEADER, ADMIN_TOKEN_STORAGE_KEY, type AdminLeadRecord } from "@/lib/admin";
import { getRiskLevelLabel, getVerticalLabel } from "@/lib/lead-submission";

type AdminApiResponse =
  | {
      ok: true;
      items: AdminLeadRecord[];
      total: number;
    }
  | {
      ok: false;
      error: string;
      hint?: string;
    };

function formatTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function AdminApp() {
  const [tokenInput, setTokenInput] = useState("");
  const [activeToken, setActiveToken] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState<AdminLeadRecord[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  async function loadLeads(token: string) {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/leads", {
        headers: {
          [ADMIN_TOKEN_HEADER]: token,
        },
      });

      const payload = (await response.json()) as AdminApiResponse;

      if (!response.ok || !payload.ok) {
        setItems([]);
        setError(payload.error);
        return false;
      }

      setItems(payload.items);
      setActiveToken(token);
      setLastUpdatedAt(new Date().toISOString());
      return true;
    } catch (loadError) {
      console.error("Failed to load admin leads", loadError);
      setItems([]);
      setError("加载线索失败，请稍后重试。");
      return false;
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    const savedToken = window.localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)?.trim() ?? "";

    if (!savedToken) {
      setIsLoading(false);
      return;
    }

    setTokenInput(savedToken);
    void loadLeads(savedToken);
  }, []);

  useEffect(() => {
    if (!activeToken) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadLeads(activeToken);
    }, 15000);

    const handleFocusRefresh = () => {
      if (document.visibilityState === "visible") {
        void loadLeads(activeToken);
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [activeToken]);

  async function handleUnlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const token = tokenInput.trim();

    if (!token) {
      setError("先输入管理员 token。");
      return;
    }

    setIsSubmitting(true);
    const ok = await loadLeads(token);

    if (ok) {
      window.localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
    }
  }

  function handleLogout() {
    window.localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    setActiveToken("");
    setTokenInput("");
    setItems([]);
    setError("");
    setLastUpdatedAt("");
    setIsLoading(false);
    window.location.replace("/admin");
  }

  const totalLeads = items.length;
  const travelLeads = items.filter(item => item.vertical === "travel").length;
  const commerceLeads = items.filter(item => item.vertical === "commerce").length;
  const saasLeads = items.filter(item => item.vertical === "saas").length;
  const isAuthenticated = activeToken !== "";

  if (!isAuthenticated) {
    return (
      <main className="relative min-h-screen overflow-hidden">
        <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col justify-center px-5 py-10 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-3xl rounded-[2rem] border border-[#d9e8e2] bg-white/92 p-8 shadow-[0_30px_80px_rgba(33,53,47,0.12)] backdrop-blur">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#eef6f3] px-4 py-2 text-sm text-[#1f5147]">
              <ShieldCheck className="size-4" />
              LLMAD Admin
            </div>

            <div className="space-y-3">
              <h1 className="font-display text-5xl leading-tight text-slate-950">管理员登录</h1>
              <p className="max-w-2xl text-base leading-8 text-slate-600">
                这是一个受 token 保护的最小后台。未登录状态下只显示这个页面；输入正确 token 后才会加载线索列表。
              </p>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#fff1f0] px-4 py-2 text-sm text-[#9a433c]">
              <AlertTriangle className="size-4" />
              当前状态：未登录
            </div>

            <form className="mt-8 flex flex-col gap-4" onSubmit={handleUnlock}>
              <label className="space-y-2">
                <span className="text-sm font-medium text-slate-700">管理员 Token</span>
                <Input
                  type="password"
                  value={tokenInput}
                  onChange={event => setTokenInput(event.target.value)}
                  placeholder="粘贴管理员 token"
                  className="h-12 rounded-2xl border-slate-300 bg-[#fcfcfa] text-base"
                />
              </label>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-12 rounded-2xl bg-[#1f5147] text-base text-white hover:bg-[#173d36]"
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle className="size-4 animate-spin" />
                    验证中
                  </>
                ) : (
                  <>
                    <KeyRound className="size-4" />
                    进入后台
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-[#fafaf8] px-4 py-4 text-sm leading-7 text-slate-600">
              token 只存你当前浏览器的 localStorage，请通过{" "}
              <code className="rounded bg-[#f2f4f7] px-1.5 py-0.5 font-mono text-xs text-slate-700">
                {ADMIN_TOKEN_HEADER}
              </code>{" "}
              请求头发给后台，不要写进前端代码。
            </div>

            {error && (
              <div className="mt-4 flex items-start gap-3 rounded-2xl border border-[#f0c3bf] bg-[#fff5f4] px-4 py-3 text-sm text-[#9a433c]">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col gap-8 px-5 py-6 sm:px-8 lg:px-10 lg:py-8">
        <header className="flex flex-col gap-4 rounded-[2rem] border border-white/60 bg-white/78 px-6 py-6 shadow-[0_24px_64px_rgba(33,53,47,0.10)] backdrop-blur md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#eef6f3] px-3 py-1 text-sm text-[#1f5147]">
              <ShieldCheck className="size-4" />
              LLMAD Admin
            </div>
            <div>
              <h1 className="font-display text-4xl text-slate-950">线索后台</h1>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                这是 MVP 阶段的最小后台，只做一件事：让你和朋友能安全地看提交进来的线索。
              </p>
              <p className="mt-1 text-xs tracking-[0.18em] text-slate-400 uppercase">
                自动刷新已开启，切回页面或每 15 秒会重新拉一次数据
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-full px-3 py-1 text-sm ${
                isAuthenticated ? "bg-[#eef6f3] text-[#1f5147]" : "bg-[#fff1f0] text-[#9a433c]"
              }`}
            >
              {isAuthenticated ? "已登录" : "未登录"}
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={() => void loadLeads(activeToken)}
              disabled={!isAuthenticated || isLoading}
              className="rounded-full border-slate-200 bg-white"
            >
              {isLoading ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              刷新
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleLogout}
              className="rounded-full border-slate-200 bg-white text-slate-700"
            >
              <LogOut className="size-4" />
              清除 token
            </Button>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card className="border-white/60 bg-[#f4fbf8]/88">
            <CardContent className="space-y-2 px-5 py-1">
              <p className="text-3xl font-semibold text-slate-950">{totalLeads}</p>
              <p className="text-sm text-slate-600">总线索数</p>
            </CardContent>
          </Card>
          <Card className="border-white/60 bg-[#fff8ef]/88">
            <CardContent className="space-y-2 px-5 py-1">
              <p className="text-3xl font-semibold text-slate-950">{travelLeads}</p>
              <p className="text-sm text-slate-600">旅游线索</p>
            </CardContent>
          </Card>
          <Card className="border-white/60 bg-[#f5f8ff]/88">
            <CardContent className="space-y-2 px-5 py-1">
              <p className="text-3xl font-semibold text-slate-950">{commerceLeads}</p>
              <p className="text-sm text-slate-600">电商 / 本地生活</p>
            </CardContent>
          </Card>
          <Card className="border-white/60 bg-[#fdf8fb]/88">
            <CardContent className="space-y-2 px-5 py-1">
              <p className="text-3xl font-semibold text-slate-950">{saasLeads}</p>
              <p className="text-sm text-slate-600">企业 SaaS</p>
            </CardContent>
          </Card>
        </section>

        <Card className="border-white/60 bg-white/84 shadow-[0_20px_46px_rgba(45,33,18,0.08)]">
          <CardHeader className="gap-3">
            <CardTitle className="flex items-center gap-2 text-xl text-slate-950">
              <Users className="size-5 text-[#1f5147]" />
              最新线索
            </CardTitle>
            <CardDescription>
              默认按提交时间倒序展示最近 100 条。
              {lastUpdatedAt ? ` 最近同步：${formatTime(lastUpdatedAt)}` : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#fafaf8] px-4 py-6 text-slate-500">
                <LoaderCircle className="size-4 animate-spin" />
                正在加载后台数据
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-[#fafaf8] px-4 py-8 text-center text-slate-500">
                当前还没有线索。
              </div>
            ) : (
              items.map(item => (
                <div key={item.id} className="rounded-[1.5rem] border border-slate-200 bg-[#fffefb] p-5 shadow-sm">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#eef6f3] px-3 py-1 text-xs font-medium text-[#1f5147]">
                          {getVerticalLabel(item.vertical)}
                        </span>
                        <span className="rounded-full bg-[#f7f1e8] px-3 py-1 text-xs font-medium text-[#8a5b1f]">
                          {getRiskLevelLabel(item.riskLevel)}
                        </span>
                        <span className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600">
                          {item.status}
                        </span>
                      </div>
                      <h2 className="text-xl font-semibold text-slate-950">{item.name}</h2>
                      <p className="text-sm leading-7 text-slate-600">{item.notes}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8f7f1] px-4 py-3 text-sm text-slate-600">
                      <p>提交于 {formatTime(item.createdAt)}</p>
                      <p className="mt-1">来源 {item.source}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <div className="rounded-2xl bg-[#f8f7f1] p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">联系人</p>
                      <p className="mt-1 text-sm font-medium text-slate-900">{item.contactName}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8f7f1] p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">联系方式</p>
                      <p className="mt-1 text-sm font-medium break-all text-slate-900">{item.contactHandle}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8f7f1] p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">线索 ID</p>
                      <p className="mt-1 text-sm font-medium break-all text-slate-900">{item.id}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

export default AdminApp;
